import RefreshIcon from 'assets/icons/RefreshIcon';
import BaseButton from 'components/ui/BaseButton';
import { ATTRIB } from 'constants/test';
import { t } from 'services/i18n.service';
import { useAppDispatch, useAppSelectorShallow } from 'store/hooks';
import { setHasDataLoadFailed } from 'store/reducer/general/slice';
import {
  hasDataLoadFailedSelector,
  isOfflineSelector
} from 'store/reducer/general/selector';

interface RefreshFabProps {
  /** Re-fetch the current route's data. */
  onRefresh: () => void;
  /**
   * Render as a banner in the page flow (sticky to the top of the scroll area)
   * instead of a floating button, so it never covers table columns or buttons.
   */
  isInline?: boolean;
}

const RefreshFab = ({ onRefresh, isInline = false }: RefreshFabProps) => {
  const dispatch = useAppDispatch();
  const { hasDataLoadFailed } = useAppSelectorShallow(
    hasDataLoadFailedSelector
  );
  const { isOffline } = useAppSelectorShallow(isOfflineSelector);

  if (!hasDataLoadFailed && !isOffline) {
    return null;
  }

  const message = isOffline
    ? t('refresh_fab.you_are_offline')
    : t('refresh_fab.data_failed_to_load');

  return (
    <div
      role='status'
      className={
        isInline
          ? 'sticky top-0 z-40 w-full flex flex-wrap items-center justify-center ~gap-2/3 ~px-3/4 ~py-1.5/2 bg-white dark:bg-secondary-dark-200 border-b border-gray-200 dark:border-secondary-dark-300'
          : 'fixed ~bottom-4/6 ~right-4/12 z-50 flex flex-col items-center ~gap-2/3'
      }>
      <p
        className={
          isInline
            ? '~text-xs/sm text-secondary-dark-300 dark:text-secondary-200'
            : '~text-xs/sm text-secondary-dark-300 dark:text-secondary-200 bg-white/90 dark:bg-secondary-dark-200/90 ~px-2/3 ~py-1/1.5 rounded-md shadow-sm max-w-[16rem] md:max-w-[20rem] text-center'
        }>
        {message}
      </p>
      <BaseButton
        size='md'
        icon={
          <RefreshIcon
            className='~w-3/6 ~h-3/6'
            fill='currentColor'
            fillDarkMode='currentColor'
          />
        }
        title={t('refresh_fab.refresh_data')}
        aria-label={t('refresh_fab.refresh_data')}
        onClick={() => {
          dispatch(setHasDataLoadFailed(false));
          onRefresh();
        }}
        additionalStyles='!rounded-full shadow-lg'
        data-testid={ATTRIB.DATA_TESTID.REFRESH_FAB}
      />
    </div>
  );
};

export default RefreshFab;
